import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../services/contexts/AuthContext';
import { 
  Home,
  Heart, 
  Activity, 
  User, 
  FileText, 
  BarChart3,
  Calendar,
  MessageCircle,
  Settings,
  Star,
  TrendingUp,
  CheckCircle,
  Clock,
  Shield,
  Target,
  Zap,
  Award
} from 'lucide-react';

const TenantPatientSidebar = ({ activeView, setActiveView }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      id: 'dashboard',
      label: 'My Health Dashboard',
      icon: Home,
      color: 'text-[#207CE5]',
      path: '/tenant-patient/dashboard'
    },
    {
      id: 'vitals',
      label: 'My Vital Signs',
      icon: Heart,
      color: 'text-[#008B8B]',
      path: '/tenant-patient/vitals'
    },
    {
      id: 'recordings',
      label: 'Recording History',
      icon: Activity,
      color: 'text-[#207CE5]',
      path: '/tenant-patient/recordings'
    },
    {
      id: 'health-insights',
      label: 'Health Insights',
      icon: BarChart3,
      color: 'text-[#00BCD4]',
      path: '/tenant-patient/health-insights'
    },
    {
      id: 'profile',
      label: 'My Profile',
      icon: User,
      color: 'text-[#5B7DAE]',
      path: '/tenant-patient/profile'
    },
    {
      id: 'appointments',
      label: 'Appointments',
      icon: Calendar,
      color: 'text-[#008B8B]',
      path: '/tenant-patient/appointments'
    }
  ];

  const handleNavigation = (item) => {
    setActiveView(item.id);
    navigate(item.path);
  };

  const getCurrentPath = () => {
    return location.pathname.split('/').pop();
  };

  return (
    <div className="w-80 bg-white shadow-lg h-screen overflow-y-auto flex flex-col border-r border-[#E6EEF4]">
      {/* Logo and Brand */}
      <div className="p-6 border-b border-[#E6EEF4]">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-[#207CE5] to-[#008B8B] rounded-xl flex items-center justify-center shadow-lg">
              <Heart className="h-7 w-7 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#207CE5] rounded-full border-2 border-white">
              <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
            </div>
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#0B1C2C]">VitalAudio</h1>
            <p className="text-sm text-[#3D3D3D] font-medium">Personal Health</p>
          </div>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="p-4">
        <div className="bg-gradient-to-r from-[#E6EEF4] to-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#207CE5] to-[#008B8B] rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">
                {user?.name?.split(' ').map(n => n[0]).join('') || 'P'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0B1C2C] truncate">
                {user?.name || 'Patient'}
              </p>
              <p className="text-xs text-[#3D3D3D]">Personal Health Account</p>
              <div className="flex items-center mt-1">
                <div className="w-2 h-2 bg-[#207CE5] rounded-full mr-2"></div>
                <span className="text-xs text-[#207CE5] font-medium">Active Patient</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-4 space-y-1">
        <div className="mb-4">
          <p className="text-xs font-semibold text-[#5B7DAE] uppercase tracking-wide mb-3">
            MY HEALTH
          </p>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = getCurrentPath() === item.id || (getCurrentPath() === '' && item.id === 'dashboard');
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  isActive 
                    ? 'bg-[#E6EEF4] text-[#207CE5] border border-[#207CE5] shadow-sm' 
                    : 'text-[#3D3D3D] hover:text-[#0B1C2C] hover:bg-[#F9FBFC]'
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-[#207CE5]' : item.color} transition-colors`} />
                <span className="font-medium">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-2 h-2 bg-[#207CE5] rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Health Status Summary */}
      <div className="p-4">
        <div className="bg-gradient-to-r from-[#E6EEF4] to-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B1C2C]">Health Summary</h3>
            <Star className="h-4 w-4 text-[#008B8B]" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Latest Recording</span>
              <div className="flex items-center space-x-1">
                <CheckCircle className="h-3 w-3 text-[#008B8B]" />
                <span className="text-xs font-medium text-[#008B8B]">Complete</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Heart Health</span>
              <div className="flex items-center space-x-1">
                <Heart className="h-3 w-3 text-[#008B8B]" />
                <span className="text-xs font-medium text-[#008B8B]">Good</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Next Check-up</span>
              <div className="flex items-center space-x-1">
                <Clock className="h-3 w-3 text-[#207CE5]" />
                <span className="text-xs font-medium text-[#207CE5]">In 2 weeks</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Health Goals */}
      <div className="p-4">
        <div className="bg-gradient-to-r from-[#E6EEF4] to-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B1C2C]">Health Goals</h3>
            <Target className="h-4 w-4 text-[#207CE5]" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#3D3D3D]">Weekly Recordings</span>
              <span className="font-bold text-[#207CE5]">3/3</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#3D3D3D]">Consistency Score</span>
              <span className="font-bold text-[#008B8B]">95%</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#3D3D3D]">Health Streak</span>
              <span className="font-bold text-[#207CE5]">12 days</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="p-4">
        <div className="bg-gradient-to-r from-[#E6EEF4] to-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B1C2C]">Recent Activity</h3>
            <Zap className="h-4 w-4 text-[#207CE5]" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center text-xs text-[#207CE5]">
              <CheckCircle className="h-3 w-3 mr-2" />
              <span>Completed vital recording</span>
            </div>
            <div className="flex items-center text-xs text-[#207CE5]">
              <Award className="h-3 w-3 mr-2" />
              <span>Achieved 7-day streak</span>
            </div>
            <div className="flex items-center text-xs text-[#207CE5]">
              <TrendingUp className="h-3 w-3 mr-2" />
              <span>Health score improved</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-[#E6EEF4]">
        <div className="text-center">
          <p className="text-xs text-[#5B7DAE]">
            VitalAudio Patient v1.0.0
          </p>
          <p className="text-xs text-[#5B7DAE]">
            © 2025 Healthcare Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default TenantPatientSidebar;